import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Grid,
    CircularProgress,
    Chip,
    Alert
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig } from '../../../redux/slices/config';
import { getPushStatus } from '../../../redux/ducks/push';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';
import { useBrandName } from '../../../utils/brandUtils';

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SectionCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const ToggleRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ActionsRow = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
});

const SectionHelper = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ReadOnlyCard = styled(SectionCard)(({ theme }) => ({
    marginTop: theme.spacing(3),
    borderStyle: 'dashed',
}));

const HeadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const StatusRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const DetailRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
    minWidth: 150,
    color: theme.palette.text.secondary,
}));

const DetailValue = styled(Typography)({
    fontFamily: 'monospace',
    wordBreak: 'break-all',
});

const CodeBlock = styled(Box)(({ theme }) => ({
    fontFamily: 'monospace',
    fontSize: '0.8125rem',
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1.5),
    margin: theme.spacing(1, 0),
    overflowX: 'auto',
    whiteSpace: 'pre',
}));

const Notifications = () => {
    const dispatch = useDispatch();
    const brandName = useBrandName();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [emailConfig, setEmailConfig] = useState({
        service: '',
        host: '',
        port: 587,
        authUser: '',
        authPassword: '',
        fromEmail: '',
        fromName: '',
        testEmail: '',
        secure: true,
        enabled: false,
    });
    const [appriseConfig, setAppriseConfig] = useState({
        url: '',
        enabled: false,
    });
    const [pushStatus, setPushStatus] = useState(null);
    const [pushStatusDenied, setPushStatusDenied] = useState(false);
    const [pushStatusLoading, setPushStatusLoading] = useState(true);

    useEffect(() => {
        loadConfig();
        dispatch(getPushStatus((data) => {
            if (!data || data.ok === false) {
                setPushStatus(null);
                setPushStatusDenied(data?.status === 403);
            } else {
                setPushStatus(data);
                setPushStatusDenied(false);
            }
            setPushStatusLoading(false);
        }));
    }, []);

    const loadConfig = () => {
        setLoading(true);
        let pending = 2;
        const done = () => { pending -= 1; if (pending <= 0) setLoading(false); };
        dispatch(getConfigValue({
            key: 'emailConfig',
            value: null,
            callback: (data) => {
                if (data?.value) setEmailConfig(prev => ({ ...prev, ...data.value }));
                done();
            }
        }));
        dispatch(getConfigValue({
            key: 'appriseConfig',
            value: null,
            callback: (data) => {
                if (data?.value) setAppriseConfig(prev => ({ ...prev, ...data.value }));
                done();
            }
        }));
    };

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const missingPushVars = [];
    if (pushStatus && !pushStatus.publicKeySet) missingPushVars.push('VAPID_PUBLIC_KEY');
    if (pushStatus && !pushStatus.privateKeySet) missingPushVars.push('VAPID_PRIVATE_KEY');

    const pushUserWord = pushStatus && pushStatus.subscriberCount === 1 ? 'user' : 'users';
    const pushDeviceWord = pushStatus && pushStatus.deviceCount === 1 ? 'device' : 'devices';
    const pushCountsLabel = pushStatus ? `${pushStatus.subscriberCount} ${pushUserWord}, ${pushStatus.deviceCount} ${pushDeviceWord} subscribed` : '';

    const handleEmailChange = (e) => {
        setIsDirty(true);
        const { name, value, checked, type } = e.target;
        setEmailConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAppriseChange = (e) => {
        setIsDirty(true);
        const { name, value, checked, type } = e.target;
        setAppriseConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = () => {
        setSaving(true);
        let pending = 2;
        let hadError = false;
        const done = (data) => {
            if (data?.status === 0) hadError = true;
            pending -= 1;
            if (pending <= 0) {
                setSaving(false);
                if (!hadError) {
                    setIsDirty(false);
                    dispatch(showAlert({ severity: 'success', message: 'Notification settings saved successfully' }));
                }
            }
        };
        dispatch(saveConfig({ key: 'emailConfig', value: emailConfig, callback: done }));
        dispatch(saveConfig({ key: 'appriseConfig', value: appriseConfig, callback: done }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Configuration"
                subtitle="Manage system configuration settings"
            />

            <ConfigSubMenu />

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : (
                <>
                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Email Configuration
                        </Typography>
                        <ToggleRow>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="enabled"
                                        checked={emailConfig.enabled}
                                        onChange={handleEmailChange}
                                    />
                                }
                                label="Enable email notifications"
                            />
                        </ToggleRow>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Service"
                                    name="service"
                                    value={emailConfig.service}
                                    onChange={handleEmailChange}
                                    placeholder="e.g., Mailgun, Gmail"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="SMTP Host"
                                    name="host"
                                    value={emailConfig.host}
                                    onChange={handleEmailChange}
                                    placeholder="smtp.mailgun.org"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="SMTP Port"
                                    name="port"
                                    type="number"
                                    value={emailConfig.port}
                                    onChange={handleEmailChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Auth User"
                                    name="authUser"
                                    value={emailConfig.authUser}
                                    onChange={handleEmailChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Auth Password"
                                    name="authPassword"
                                    type="password"
                                    value={emailConfig.authPassword}
                                    onChange={handleEmailChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="From Email"
                                    name="fromEmail"
                                    value={emailConfig.fromEmail}
                                    onChange={handleEmailChange}
                                    placeholder="noreply@example.com"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="From Name"
                                    name="fromName"
                                    value={emailConfig.fromName}
                                    onChange={handleEmailChange}
                                    placeholder={brandName}
                                    helperText="Display name recipients see next to the from address"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Test Email"
                                    name="testEmail"
                                    value={emailConfig.testEmail}
                                    onChange={handleEmailChange}
                                    helperText="Email address for sending test notifications"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="secure"
                                            checked={emailConfig.secure}
                                            onChange={handleEmailChange}
                                        />
                                    }
                                    label="Use TLS/SSL"
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Apprise Configuration
                        </Typography>
                        <ToggleRow>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="enabled"
                                        checked={appriseConfig.enabled}
                                        onChange={handleAppriseChange}
                                    />
                                }
                                label="Enable Apprise notifications"
                            />
                        </ToggleRow>

                        <TextField
                            fullWidth
                            label="Apprise Service URL"
                            name="url"
                            value={appriseConfig.url}
                            onChange={handleAppriseChange}
                            placeholder="http://apprise-host:8000/notify/"
                            helperText="URL of the Apprise notification service"
                        />
                    </SectionCard>

                    <ActionsRow>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </ActionsRow>

                    <ReadOnlyCard>
                        <HeadingRow>
                            <Typography variant="h6">
                                Push Configuration
                            </Typography>
                            <Chip size="small" variant="outlined" label="Read-only" />
                        </HeadingRow>
                        <SectionHelper variant="body2" color="text.secondary">
                            Browser push notifications are switched on by environment variables on the {brandName} API server, not from this page, so there is nothing
                            here to edit or save. Changing them means updating the server environment and restarting the API.
                        </SectionHelper>

                        {pushStatusLoading ? (
                            <CircularProgress size={20} />
                        ) : pushStatusDenied ? (
                            <Alert severity="info">
                                You do not have permission to view the push configuration. Ask someone with the system settings permission to check it for you.
                            </Alert>
                        ) : !pushStatus ? (
                            <Alert severity="warning">
                                The server did not report its push configuration. Reload the page, and if it keeps failing check that the API is running.
                            </Alert>
                        ) : (
                            <>
                                <StatusRow>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        color={pushStatus.configured ? 'success' : 'warning'}
                                        label={pushStatus.configured ? 'Push is configured on this server' : 'Push is not configured on this server'}
                                    />
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        color={pushStatus.deviceCount > 0 ? 'primary' : 'default'}
                                        label={pushCountsLabel}
                                    />
                                </StatusRow>

                                {pushStatus.configured ? (
                                    <>
                                        <DetailRow>
                                            <DetailLabel variant="body2">VAPID_SUBJECT</DetailLabel>
                                            <DetailValue variant="body2">
                                                {pushStatus.subject}
                                                {pushStatus.subjectSet ? '' : ' (built-in default, variable not set)'}
                                            </DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel variant="body2">VAPID_PUBLIC_KEY</DetailLabel>
                                            <DetailValue variant="body2">{pushStatus.publicKey}</DetailValue>
                                        </DetailRow>
                                        <DetailRow>
                                            <DetailLabel variant="body2">VAPID_PRIVATE_KEY</DetailLabel>
                                            <DetailValue variant="body2">Set on the server, never displayed</DetailValue>
                                        </DetailRow>
                                        {pushStatus.deviceCount === 0 && (
                                            <Alert severity="info">
                                                Push is ready, but no one has subscribed a device yet, so nothing is being delivered. Each person turns push on for
                                                themselves from their Settings page, once per browser or device.
                                            </Alert>
                                        )}
                                    </>
                                ) : (
                                    <Alert severity="warning">
                                        <Typography variant="body2">
                                            Push notifications are disabled because the API server is missing {missingPushVars.join(' and ')}. Generate a key pair on the
                                            server with:
                                        </Typography>
                                        <CodeBlock>npx web-push generate-vapid-keys</CodeBlock>
                                        <Typography variant="body2">
                                            Then set VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY in the API container environment, optionally VAPID_SUBJECT (a mailto:
                                            address the push service can use to reach you), and restart the API. Email and in-app notifications are unaffected.
                                        </Typography>
                                    </Alert>
                                )}
                            </>
                        )}
                    </ReadOnlyCard>
                </>
            )}
            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default Notifications;
